<template>
    <div class="crm-overview">
        <div class="stats-row">
            <b-card class="stat-card">
                <div class="stat-label">CURRENT PLAN</div>
                <div class="stat-value">{{ overview.currentPlan }}</div>
                <div class="stat-detail">
                    Renews on {{ overview.renewDate }}
                </div>
            </b-card>
            <b-card class="stat-card">
                <div class="stat-label">OUTSTANDING BALANCE</div>
                <div class="stat-value stat-value-danger">
                    ${{ overview.outstandingBalance }}
                </div>
                <b-badge pill class="badge-no-overdue">No Overdue</b-badge>
            </b-card>
            <b-card class="stat-card">
                <div class="stat-label">LAST INVOICE</div>
                <div class="stat-value">${{ overview.lastInvoiceAmount }}</div>
                <div class="stat-detail">
                    Paid on {{ overview.lastInvoicePaidDate }}
                </div>
            </b-card>
            <b-card class="stat-card">
                <div class="stat-label">OPEN TICKETS</div>
                <div class="stat-value stat-value-blue">
                    {{ overview.openTickets }}
                </div>
                <div class="stat-detail">{{ overview.openTicketsDetail }}</div>
            </b-card>
        </div>

        <div class="overview-row">
            <b-card class="overview-card basic-info-card">
                <div class="card-title-row">
                    <span class="card-label">BASIC INFO</span>
                    <b-button variant="link" class="p-0 edit-link"
                        >Edit</b-button
                    >
                </div>
                <div class="info-list">
                    <div class="info-item">
                        <i class="fa fa-user info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">CONTACT NAME</div>
                            <div class="info-value">
                                {{ basicInfo.contactName }}
                            </div>
                        </div>
                    </div>
                    <div class="info-item">
                        <i class="fa fa-envelope info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">EMAIL ADDRESS</div>
                            <div class="info-value info-value-email">{{ basicInfo.email }}</div>
                        </div>
                    </div>
                    <div class="info-item">
                        <i class="fa fa-phone info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">PHONE NUMBER</div>
                            <div class="info-value">{{ basicInfo.phone }}</div>
                        </div>
                    </div>
                    <div class="info-item">
                        <i class="fa fa-building info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">COMPANY</div>
                            <div class="info-value">
                                {{ basicInfo.company }}
                            </div>
                        </div>
                    </div>
                    <div class="info-item">
                        <i class="fa fa-shield info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">MANAGER</div>
                            <div class="info-value">
                                {{ basicInfo.manager }}
                            </div>
                        </div>
                    </div>
                    <div class="info-item">
                        <i class="fa fa-calendar info-icon"></i>
                        <div class="info-content">
                            <div class="info-label">JOINED ON</div>
                            <div class="info-value">
                                {{ basicInfo.joinedOn }}
                            </div>
                        </div>
                    </div>
                </div>
            </b-card>
            <b-card class="overview-card recent-invoices-card">
                <div class="card-title-row">
                    <span class="card-label">RECENT INVOICES</span>
                    <b-button
                        variant="link"
                        class="p-0 edit-link"
                        @click="$emit('show-invoices')"
                        >VIEW ALL</b-button
                    >
                </div>
                <div class="invoice-list">
                    <div
                        v-for="inv in recentInvoices"
                        :key="inv.id"
                        class="invoice-item"
                    >
                        <i class="fa fa-file-text-o invoice-icon"></i>
                        <div class="invoice-meta">
                            <span class="invoice-number">{{ inv.number }}</span>
                            <span class="invoice-date">{{ inv.date }}</span>
                        </div>
                        <div class="invoice-right">
                            <span class="invoice-amount"
                                >${{ inv.amount }}</span
                            >
                            <b-badge pill class="invoice-badge paid"
                                >Paid</b-badge
                            >
                            <b-button
                                variant="link"
                                class="p-0 ml-1 download-btn"
                            >
                                <i class="fa fa-download"></i>
                            </b-button>
                        </div>
                    </div>
                </div>
            </b-card>
        </div>

        <div class="overview-row">
            <b-card class="overview-card subscription-card">
                <div class="card-label mb-3">CURRENT SUBSCRIPTION</div>
                <div class="subscription-plan-name">
                    {{ subscription.planName }}
                </div>
                <b-badge pill class="subscription-status ml-2">ACTIVE</b-badge>
                <div class="subscription-billing">
                    {{ subscription.billing }}
                </div>
                <div class="seats-row">
                    <span class="seats-label">Seats Used</span>
                    <span class="seats-value"
                        >{{ subscription.seatsUsed }} /
                        {{ subscription.seatsTotal }}</span
                    >
                </div>
                <b-progress
                    :value="subscription.seatsUsed"
                    :max="subscription.seatsTotal"
                    class="seats-progress mb-3"
                ></b-progress>
                <b-button
                    variant="primary"
                    block
                    class="btn-upgrade-block"
                    @click="$emit('upgrade')"
                    >UPGRADE PLAN</b-button
                >
            </b-card>
            <b-card class="overview-card recent-tickets-card">
                <div class="card-title-row">
                    <span class="card-label">RECENT TICKETS</span>
                    <b-button
                        variant="link"
                        class="p-0 edit-link"
                        @click="$emit('show-tickets')"
                        >VIEW ALL</b-button
                    >
                </div>
                <div class="ticket-list">
                    <div
                        v-for="ticket in recentTickets"
                        :key="ticket.id"
                        class="ticket-item"
                    >
                        <b-badge pill class="ticket-id-badge"
                            >#{{ ticket.id }}</b-badge
                        >
                        <div class="ticket-content">
                            <div class="ticket-title">{{ ticket.title }}</div>
                            <div class="ticket-date">
                                Updated {{ ticket.updated }}
                            </div>
                        </div>
                        <div class="ticket-badges">
                            <b-badge
                                :pill="true"
                                :variant="ticket.priorityVariant"
                                class="priority-badge"
                                >{{ ticket.priority }}</b-badge
                            >
                            <b-badge
                                pill
                                variant="light"
                                class="status-badge-ticket"
                                >{{ ticket.status }}</b-badge
                            >
                        </div>
                    </div>
                </div>
            </b-card>
        </div>
    </div>
</template>

<script>
export default {
    name: "CrmOverview",
    props: {
        accountId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            overview: {
                currentPlan: "Enterprise Plan",
                renewDate: "Mar 01, 2026",
                outstandingBalance: "0.00",
                lastInvoiceAmount: "1,200.00",
                lastInvoicePaidDate: "Feb 02, 2026",
                openTickets: 2,
                openTicketsDetail: "1 Urgent, 1 Normal",
            },
            basicInfo: {
                contactName: "Biswarup Roy",
                email: "biswarup.roy@calonex.com",
                phone: "+1 (555) 123-4567",
                company: "Calonex Solutions",
                manager: "Sarah Johnson",
                joinedOn: "Jun 15, 2025",
            },
            recentInvoices: [
                {
                    id: 1,
                    number: "INV-2026-001",
                    date: "2026-02-01",
                    amount: "1,200",
                },
                {
                    id: 2,
                    number: "INV-2025-012",
                    date: "2025-12-01",
                    amount: "1,150",
                },
            ],
            subscription: {
                planName: "Enterprise Plan",
                billing: "Monthly Billing",
                seatsUsed: 12,
                seatsTotal: 20,
            },
            recentTickets: [
                {
                    id: "SR1523",
                    title: "Dashboard modification request",
                    updated: "2026-02-10",
                    priority: "High",
                    priorityVariant: "warning",
                    status: "In Progress",
                },
                {
                    id: "SR1410",
                    title: "Billing interval change",
                    updated: "2026-01-15",
                    priority: "Medium",
                    priorityVariant: "info",
                    status: "Closed",
                },
            ],
        };
    },
};
</script>

<style scoped>
.crm-overview {
    overflow-x: hidden;
    max-width: 100%;
}

.stats-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 24px;
}

.stat-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 20px;
}

.stat-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1d21;
}

.stat-value-danger {
    color: #dc3545;
}

.stat-value-blue {
    color: #1565c0;
}

.stat-detail {
    font-size: 0.9rem;
    color: #495057;
    margin-top: 4px;
}

.badge-no-overdue {
    background-color: #d4edda !important;
    color: #155724 !important;
    font-size: 0.75rem;
    margin-top: 8px;
}

.overview-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
}

.overview-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 24px;
    overflow-x: hidden;
}

.card-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.card-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
}

.edit-link {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1565c0;
}

.info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.info-icon {
    color: #6c757d;
    width: 20px;
    margin-top: 2px;
    flex-shrink: 0;
}

.info-content {
    min-width: 0;
    flex: 1;
}

.info-value-email {
    word-break: break-word;
    overflow-wrap: break-word;
}

.info-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 2px;
}

.info-value {
    font-weight: 600;
    color: #1a1d21;
    word-break: break-word;
    overflow-wrap: break-word;
}

.invoice-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.invoice-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f3f5;
    transition: background 0.2s;
    min-width: 0;
}

.invoice-item:hover {
    background: #f8f9fa;
    margin: 0 -12px;
    padding: 12px;
    border-radius: 8px;
}

.invoice-item:last-child {
    border-bottom: 0;
}

.invoice-icon {
    color: #6c757d;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.invoice-right {
    flex-shrink: 0;
}

.invoice-meta {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.invoice-number {
    font-weight: 700;
    color: #1a1d21;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.invoice-date {
    font-size: 0.85rem;
    color: #6c757d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.invoice-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.invoice-amount {
    font-weight: 700;
    color: #1a1d21;
}

.invoice-badge.paid {
    background-color: #d4edda !important;
    color: #155724 !important;
}

.download-btn {
    color: #6c757d;
}

.subscription-plan-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1d21;
    display: inline;
}

.subscription-status {
    background-color: #1565c0 !important;
    color: #fff !important;
}

.subscription-billing {
    font-size: 0.9rem;
    color: #6c757d;
    margin-top: 4px;
}

.seats-row {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    font-size: 0.9rem;
}

.seats-label {
    color: #6c757d;
}

.seats-value {
    font-weight: 600;
    color: #1a1d21;
}

.seats-progress >>> .progress-bar {
    background-color: #495057;
}

.btn-upgrade-block {
    border-radius: 8px;
    font-weight: 600;
}

.ticket-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ticket-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f3f5;
    transition: background 0.2s;
}

.ticket-item:hover {
    background: #f8f9fa;
    margin: 0 -12px;
    padding: 12px;
    border-radius: 8px;
}

.ticket-item:last-child {
    border-bottom: 0;
}

.ticket-id-badge {
    background-color: #e3f2fd !important;
    color: #1565c0 !important;
    flex-shrink: 0;
}

.ticket-content {
    flex: 1;
    min-width: 0;
}

.ticket-title {
    font-weight: 700;
    color: #1a1d21;
    word-break: break-word;
    overflow-wrap: break-word;
}

.ticket-date {
    font-size: 0.85rem;
    color: #6c757d;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ticket-badges {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.status-badge-ticket {
    color: #495057;
}

@media (max-width: 992px) {
    .stats-row {
        grid-template-columns: repeat(2, 1fr);
    }

    .overview-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .stats-row {
        grid-template-columns: 1fr;
    }

    .overview-card {
        padding: 20px 16px;
    }

    .invoice-item {
        flex-wrap: wrap;
        gap: 8px;
    }

    .invoice-meta {
        flex: 1 1 auto;
        min-width: 80px;
    }

    .invoice-right {
        flex: 1 1 100%;
        justify-content: flex-start;
        padding-top: 4px;
    }

    .ticket-item {
        flex-wrap: wrap;
    }

    .ticket-content {
        flex: 1 1 auto;
        min-width: 100px;
    }

    .ticket-badges {
        width: 100%;
        margin-top: 8px;
    }
}
</style>
